import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Lozenge from '@atlaskit/lozenge';
import { ButtonItem } from '@atlaskit/menu';
import { Flex, Box, xcss } from '@atlaskit/primitives';

import { ExternalUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozenge';

import type { Participant, PresenceContentMode, PresenceContentType } from '../presenceTypes';
import { useScrollToTelepointer } from '../useScrollToTelepointer';

import { PresenceAvatar } from './PresenceAvatar';

const i18n = defineMessages({
	editing: {
		id: 'team-presence.menu.editing',
		defaultMessage: 'Editing',
		description:
			'The text is shown as a title of a section inside of a menu. The people in the section are editing a page',
	},
	viewing: {
		id: 'team-presence.menu.viewing',
		defaultMessage: 'Viewing',
		description: 'The text is used to indicate that the current user is `Viewing the page`',
	},
	you: {
		id: 'team-presence.menu.current-user.marker',
		defaultMessage: 'You',
		description: 'The text is used to indicate that the current user is `You`',
	},
	clickToFind: {
		id: 'team-presence.menu-item.click-to-find',
		defaultMessage: 'Click to find',
		description:
			"The text is used in the presence menu item to indicate that the menu item can be clicked to locate the user's pointer in the document. When clicked, the editor scrolls to that pointer's position.",
	},
	clickToFindNotAvailable: {
		id: 'team-presence.menu-item.click-to-find-not-available',
		defaultMessage: 'Inactive',
		description:
			"If for some reason (e.g., user not focusing to document) we cannot locate the telepointer, this message shows up instead of 'clickToFind' message.",
	},
	clickToFollow: {
		id: 'team-presence.menu-item.click-to-follow',
		defaultMessage: 'Click to follow',
		description:
			"The text is used in the presence menu item to indicate that the menu item can be clicked to follow the user's pointer",
	},
});

const DESCRIPTION_SEPARATOR = ' - ';

export type PresenceMenuItemProps = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	isCurrentUser?: boolean;
	participant: Participant;
};

const hiddenStyle = xcss({ visibility: 'hidden' });
const visibleStyle = xcss({ visibility: 'visible' });

export const PresenceMenuItem = ({
	participant,
	contentMode,
	contentId,
	contentType,
	isCurrentUser,
}: PresenceMenuItemProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isScrollToFeatureEnabled, isEligibleToScroll, scrollToTelepointer } =
		useScrollToTelepointer({
			contentType,
			contentMode,
			// TODO: https://product-fabric.atlassian.net/browse/MDS-1320
			participant: participant as any,
		});

	const isAnonymous = participant.userId === 'unidentified';
	const hasOnClickEvent = isEligibleToScroll || Boolean(participant.onClick);

	const handleClick = useCallback(() => {
		let isScrollSuccessful = false;

		if (isEligibleToScroll) {
			isScrollSuccessful = scrollToTelepointer();
		}

		if (participant.onClick) {
			participant.onClick();
		}

		if (hasOnClickEvent) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					product: 'confluence',
					source: 'presenceMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'presenceMenuItem',
					attributes: {
						contentId,
						contentType,
						contentMode,
						isScrollToFeatureEnabled,
						isEligibleToScroll,
						isScrollSuccessful,
					},
				},
			}).fire();
		}
	}, [
		participant,
		contentId,
		contentType,
		contentMode,
		isScrollToFeatureEnabled,
		isEligibleToScroll,
		hasOnClickEvent,
		scrollToTelepointer,
		createAnalyticsEvent,
	]);

	// A JS-based hover interaction is usually not preferred over pure CSS,
	// but since nested CSS selectors are discouraged (see the "no-nested-selectors" linting rule),
	// and we don’t have control over "ButtonItem", this seems to be the fastest valid solution for now.
	const [isHovered, setHover] = useState(false);
	const handleFocus = () => {
		if (isScrollToFeatureEnabled || contentType === 'whiteboard') {
			setHover(true);
		}
	};
	const handleBlur = () => {
		if (isScrollToFeatureEnabled || contentType === 'whiteboard') {
			setHover(false);
		}
	};

	return (
		<Box
			onMouseEnter={handleFocus}
			onFocus={handleFocus}
			onMouseLeave={handleBlur}
			onBlur={handleBlur}
		>
			<ButtonItem
				onClick={handleClick}
				testId="team-presence-menu-item"
				data-vc="team-presence-menu-item"
				iconBefore={
					<PresenceAvatar
						testId="team-presence-menu-item-avatar"
						participant={participant}
						contentMode={contentMode}
						contentId={contentId}
						contentType={contentType}
						isEligibleToScroll={isEligibleToScroll}
					/>
				}
				description={
					<>
						{contentType !== 'whiteboard' &&
							(participant.activity === 'editor' ? (
								<FormattedMessage {...i18n.editing} />
							) : (
								<FormattedMessage {...i18n.viewing} />
							))}

						{contentType === 'whiteboard' && participant.followCursor && (
							<Box as="span" xcss={isHovered ? visibleStyle : hiddenStyle}>
								<FormattedMessage {...i18n.clickToFollow} />
							</Box>
						)}
						{isScrollToFeatureEnabled && (
							<Box as="span" xcss={isHovered ? visibleStyle : hiddenStyle}>
								{DESCRIPTION_SEPARATOR}
								{isEligibleToScroll ? (
									<FormattedMessage {...i18n.clickToFind} />
								) : (
									<FormattedMessage {...i18n.clickToFindNotAvailable} />
								)}
							</Box>
						)}
					</>
				}
			>
				<Flex alignItems="baseline" gap="space.050">
					{participant.name}
					{participant.isGuest && <ExternalUserLozenge />}
					{isCurrentUser && isAnonymous && contentType !== 'whiteboard' && (
						<Lozenge appearance="inprogress">
							<FormattedMessage {...i18n.you} />
						</Lozenge>
					)}
				</Flex>
			</ButtonItem>
		</Box>
	);
};
